// Common validation functions for the application

export interface ValidationResult {
  isValid: boolean;
  error: string;
}

/**
 * Validates email address format and requirement
 * @param email - The email string to validate
 * @returns Validation result with error message if invalid
 */
export const validateEmail = (email: string): string => {
  if (!email.trim()) return 'Email is required';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return 'Please enter a valid email address';
  return '';
};

/**
 * Validates password for login (basic validation)
 * @param password - The password string to validate
 * @returns Validation result with error message if invalid
 */
export const validatePassword = (password: string): string => {
  if (!password) return 'Password is required';
  if (password.length < 6) return 'Password must be at least 6 characters long';
  return '';
};

/**
 * Validates password for signup/registration (stronger validation)
 * @param password - The password string to validate
 * @returns Validation result with error message if invalid
 */
export const validatePasswordStrong = (password: string): string => {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
  if (!/(?=.*[@$!%*?&])/.test(password)) return 'Password must contain at least one special character';
  return '';
};

/**
 * Validates phone number format (optional field)
 * @param phone - The phone string to validate
 * @returns Validation result with error message if invalid
 */
export const validatePhone = (phone: string): string => {
  if (!phone.trim()) return '';
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  if (!phoneRegex.test(phone.replace(/[-\s\(\)]/g, ''))) {
    return 'Please enter a valid phone number';
  }
  return '';
};

/**
 * Validates name fields (first name, last name)
 * @param name - The name string to validate
 * @param fieldName - The field name for error message
 * @returns Validation result with error message if invalid
 */
export const validateName = (name: string, fieldName: string): string => {
  if (!name.trim()) return `${fieldName} is required`;
  if (name.trim().length < 2) return `${fieldName} must be at least 2 characters long`;
  if (!/^[a-zA-Z\s]+$/.test(name)) return `${fieldName} should only contain letters`;
  return '';
};

/**
 * Validates password confirmation
 * @param password - The original password
 * @param confirmPassword - The confirmation password
 * @returns Validation result with error message if invalid
 */
export const validateConfirmPassword = (password: string, confirmPassword: string): string => {
  if (!confirmPassword) return 'Please confirm your password';
  if (password !== confirmPassword) return 'Passwords do not match';
  return '';
};

/**
 * Generic field validator that routes to specific validation functions
 * @param fieldName - The name of the field to validate
 * @param value - The value to validate
 * @param additionalValue - Optional additional value for comparison (e.g., password for confirm password)
 * @returns Validation result with error message if invalid
 */
export const validateField = (fieldName: string, value: string, additionalValue?: string): string => {
  switch (fieldName) {
    case 'firstName':
      return validateName(value, 'First name');
    case 'lastName':
      return validateName(value, 'Last name');
    case 'email':
      return validateEmail(value);
    case 'phone':
      return validatePhone(value);
    case 'password':
      return validatePassword(value);
    case 'passwordStrong':
      return validatePasswordStrong(value);
    case 'confirmPassword':
      return validateConfirmPassword(additionalValue || '', value);
    default:
      return '';
  }
};

/**
 * Validates multiple fields and returns errors object
 * @param fields - Object with field names as keys and values to validate
 * @param strongPassword - Whether to use strong password validation
 * @returns Object with field names as keys and error messages as values
 */
export const validateMultipleFields = (
  fields: Record<string, string>,
  strongPassword: boolean = false
): Record<string, string> => {
  const errors: Record<string, string> = {};

  Object.entries(fields).forEach(([fieldName, value]) => {
    if (fieldName === 'password' && strongPassword) {
      errors[fieldName] = validatePasswordStrong(value);
    } else if (fieldName === 'confirmPassword') {
      errors[fieldName] = validateConfirmPassword(fields.password || '', value);
    } else {
      errors[fieldName] = validateField(fieldName, value);
    }
  });

  return errors;
};

/**
 * Checks if a form is valid based on errors object
 * @param errors - Object with field names as keys and error messages as values
 * @returns Boolean indicating if form is valid
 */
export const isFormValid = (errors: Record<string, string>): boolean => {
  return Object.values(errors).every(error => error === '');
};

/**
 * Gets the first field with an error for focus management
 * @param errors - Object with field names as keys and error messages as values
 * @returns The name of the first field with an error, or null if no errors
 */
export const getFirstErrorField = (errors: Record<string, string>): string | null => {
  const firstErrorField = Object.keys(errors).find(key => errors[key] !== '');
  return firstErrorField || null;
};

export const validateMobileNumber = (value: string): string => {
  const phoneRegex = /^[0-9]{10}$/;
  if (!value) {
    return 'Mobile number is required.';
  }
  if (!phoneRegex.test(value)) {
    return 'Mobile number must be 10 digits.';
  }
  return '';
};
